import { useMutation, useQuery } from "@tanstack/react-query";
import { ChevronRight, KeyRound, LogOut, Pencil, Plus, ShieldCheck, Smartphone, Trash2, UserRoundCheck, X } from "lucide-react";
import { FormEvent, ReactNode, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { PageHeader } from "../components/PageHeader";
import { RecoveryCodeNotice } from "../components/RecoveryCodeNotice";
import { ErrorScreen, LoadingScreen } from "../components/ScreenState";
import { api } from "../lib/api";
import { installInstructions, isInstalled, requestInstall } from "../lib/install";
import { queryClient } from "../lib/query";

const GRADES = [
  "幼儿园",
  "一年级",
  "二年级",
  "三年级",
  "四年级",
  "五年级",
  "六年级",
  "初一",
  "初二",
  "初三",
  "高一",
  "高二",
  "高三",
  "其他",
];

type DetailState = {
  title: string;
  icon: ReactNode;
  content: ReactNode;
};

export function ProfilePage() {
  const navigate = useNavigate();
  const data = useQuery({ queryKey: ["bootstrap"], queryFn: api.bootstrap });
  const [showForm, setShowForm] = useState(false);
  const [nickname, setNickname] = useState("");
  const [grade, setGrade] = useState("");
  const [editingId, setEditingId] = useState<string | null>(null);
  const [deletingId, setDeletingId] = useState<string | null>(null);
  const [showAccountForm, setShowAccountForm] = useState(false);
  const [accountPhone, setAccountPhone] = useState("");
  const [accountPassword, setAccountPassword] = useState("");
  const [accountNickname, setAccountNickname] = useState("");
  const [accountAgreed, setAccountAgreed] = useState(false);
  const [upgradeRecoveryCode, setUpgradeRecoveryCode] = useState("");
  const [detail, setDetail] = useState<DetailState | null>(null);
  const addChild = useMutation({
    mutationFn: () => api.createChild({ nickname: nickname.trim(), grade }),
    onSuccess: () => {
      setNickname("");
      setGrade("");
      setShowForm(false);
      void queryClient.invalidateQueries({ queryKey: ["bootstrap"] });
    },
  });
  const updateChild = useMutation({
    mutationFn: () => api.updateChild(editingId!, { nickname: nickname.trim(), grade }),
    onSuccess: () => {
      resetChildForm();
      void queryClient.invalidateQueries({ queryKey: ["bootstrap"] });
    },
  });
  const deleteChild = useMutation({
    mutationFn: (id: string) => api.deleteChild(id),
    onSuccess: () => {
      setDeletingId(null);
      void queryClient.invalidateQueries({ queryKey: ["bootstrap"] });
    },
  });
  const upgradeAccount = useMutation({
    mutationFn: () => api.register({ phone: accountPhone, password: accountPassword, nickname: accountNickname.trim(), acceptedTerms: true }),
    onSuccess: async (session) => {
      queryClient.setQueryData(["session"], session);
      setAccountPhone("");
      setAccountPassword("");
      setAccountAgreed(false);
      setUpgradeRecoveryCode(session.recoveryCode ?? "");
      await queryClient.invalidateQueries({ queryKey: ["bootstrap"] });
    },
  });
  const logout = useMutation({
    mutationFn: api.logout,
    onSuccess: () => {
      queryClient.clear();
      window.location.assign("/");
    },
  });

  if (data.isPending) return <LoadingScreen />;
  if (data.isError) return <ErrorScreen message={data.error.message} onRetry={() => void data.refetch()} />;

  function submit(event: FormEvent) {
    event.preventDefault();
    if (editingId) updateChild.mutate();
    else addChild.mutate();
  }

  function resetChildForm() {
    setNickname("");
    setGrade("");
    setEditingId(null);
    setShowForm(false);
  }

  function editChild(child: { id: string; nickname: string; grade?: string | null }) {
    setEditingId(child.id);
    setNickname(child.nickname);
    setGrade(child.grade ?? "");
    setShowForm(true);
  }

  async function openInstall() {
    if (isInstalled()) {
      setDetail({
        title: "已安装到手机",
        icon: <Smartphone size={28} />,
        content: <p className="detail-copy">作业管家当前已以主屏幕应用模式运行，无需重复安装。</p>,
      });
      return;
    }
    const result = await requestInstall();
    setDetail({
      title: result === "accepted" ? "安装已开始" : "安装到手机",
      icon: <Smartphone size={28} />,
      content: (
        <div className="detail-copy">
          <p>{result === "accepted" ? "请回到手机主屏幕查看“作业管家”图标。" : installInstructions()}</p>
          {result === "dismissed" ? <small>你刚刚取消了安装，仍可稍后再次点击此入口。</small> : null}
        </div>
      ),
    });
  }

  return (
    <section className="screen">
      <PageHeader title="我的" />
      <div className="profile-card">
        <span className="avatar large">{data.data.account.nickname.slice(0, 1)}</span>
        <div><strong>{data.data.account.nickname}</strong><p>{data.data.account.phoneMasked ?? "快速体验账号 · 数据仅凭当前会话恢复"}</p></div>
      </div>

      <div className="profile-section-title">
        <h2>孩子档案</h2>
        <button type="button" onClick={() => showForm ? resetChildForm() : setShowForm(true)}>
          <Plus size={17} /> {showForm ? "收起" : "添加"}
        </button>
      </div>
      <div className="child-list">
        {data.data.children.map((child) => (
          <div key={child.id} className="child-row">
            <span className="avatar">{child.nickname.slice(0, 1)}</span>
            <p><strong>{child.nickname}</strong><small>{child.grade || "未设置年级"}</small></p>
            <span className="child-actions">
              <button type="button" aria-label={`修改${child.nickname}的档案`} onClick={() => editChild(child)}><Pencil size={18} /></button>
              <button type="button" aria-label={`删除${child.nickname}的档案`} disabled={data.data.children.length <= 1} onClick={() => setDeletingId(child.id)}><Trash2 size={18} /></button>
            </span>
          </div>
        ))}
      </div>

      {showForm ? (
        <form className="child-form" onSubmit={submit}>
          <label>
            <span>孩子昵称</span>
            <input autoFocus value={nickname} maxLength={40} placeholder="例如：小葵" onChange={(event) => setNickname(event.target.value)} required />
          </label>
          <label>
            <span>就读年级</span>
            <select value={grade} onChange={(event) => setGrade(event.target.value)} required>
              <option value="" disabled>请选择年级</option>
              {GRADES.map((item) => <option key={item}>{item}</option>)}
            </select>
          </label>
          {addChild.isError ? <p className="form-error" role="alert">{addChild.error.message}</p> : null}
          {updateChild.isError ? <p className="form-error" role="alert">{updateChild.error.message}</p> : null}
          <div className="child-form-actions">
            <button className="secondary-button" type="button" onClick={resetChildForm}>取消</button>
            <button className="small-button" type="submit" disabled={addChild.isPending || updateChild.isPending}>{addChild.isPending || updateChild.isPending ? "保存中…" : editingId ? "保存修改" : "保存档案"}</button>
          </div>
        </form>
      ) : null}

      <div className="settings-list">
        {data.data.account.authType === "guest" ? (
          <button type="button" onClick={() => { setAccountNickname(data.data.account.nickname); setShowAccountForm(true); }}>
            <UserRoundCheck /><span><strong>注册正式账号</strong><small>保留当前数据并支持跨设备登录</small></span><ChevronRight />
          </button>
        ) : null}
        {data.data.account.authType === "account" ? (
          <button type="button" onClick={() => navigate("/account-security")}>
            <KeyRound /><span><strong>账号与安全</strong><small>称呼、密码、设备和账号注销</small></span><ChevronRight />
          </button>
        ) : null}
        <button type="button" onClick={() => navigate("/legal/privacy")}>
          <ShieldCheck /><span><strong>隐私与安全</strong><small>查看隐私政策和数据权利</small></span><ChevronRight />
        </button>
        <button type="button" onClick={() => void openInstall()}>
          <Smartphone /><span><strong>安装到手机</strong><small>安装应用或查看当前浏览器步骤</small></span><ChevronRight />
        </button>
        <button type="button" className="logout-row" disabled={logout.isPending} onClick={() => logout.mutate()}>
          <LogOut /><span><strong>退出登录</strong><small>{logout.isPending ? "正在退出…" : "返回登录与注册页面"}</small></span><ChevronRight />
        </button>
      </div>

      {deletingId ? (
        <div className="detail-backdrop" role="presentation">
          <section className="detail-sheet" role="alertdialog" aria-modal="true" aria-labelledby="delete-title">
            <span className="detail-icon danger"><Trash2 size={28} /></span>
            <h2 id="delete-title">删除孩子档案？</h2>
            <p className="detail-copy">删除后，该孩子的全部作业、计划和完成记录都会永久删除，无法恢复。</p>
            {deleteChild.isError ? <p className="form-error" role="alert">{deleteChild.error.message}</p> : null}
            <div className="dialog-actions"><button className="secondary-button" type="button" onClick={() => setDeletingId(null)}>取消</button><button className="danger-button" type="button" disabled={deleteChild.isPending} onClick={() => deleteChild.mutate(deletingId)}>{deleteChild.isPending ? "删除中…" : "确认删除"}</button></div>
          </section>
        </div>
      ) : null}

      {showAccountForm ? (
        <div className="detail-backdrop" role="presentation">
          <section className="detail-sheet" role="dialog" aria-modal="true" aria-labelledby="account-title">
            {upgradeRecoveryCode ? <RecoveryCodeNotice code={upgradeRecoveryCode} onContinue={() => { setUpgradeRecoveryCode(""); setShowAccountForm(false); }} /> : <>
              <button className="detail-close" type="button" aria-label="关闭" onClick={() => setShowAccountForm(false)}><X /></button>
              <span className="detail-icon"><UserRoundCheck size={28} /></span>
              <h2 id="account-title">注册正式账号</h2>
              <p className="detail-copy">当前孩子和作业数据会原样保留，注册后可以退出并在其他设备登录。</p>
              <form className="child-form account-upgrade-form" onSubmit={(event) => { event.preventDefault(); upgradeAccount.mutate(); }}>
              <label><span>家长称呼</span><input value={accountNickname} maxLength={40} onChange={(event) => setAccountNickname(event.target.value)} required /></label>
              <label><span>手机号</span><input inputMode="tel" autoComplete="tel" pattern="1[3-9][0-9]{9}" maxLength={11} value={accountPhone} placeholder="请输入 11 位手机号" onChange={(event) => setAccountPhone(event.target.value.replace(/\D/g, ""))} required /></label>
              <label><span>密码</span><input type="password" autoComplete="new-password" minLength={8} maxLength={64} value={accountPassword} placeholder="至少 8 个字符" onChange={(event) => setAccountPassword(event.target.value)} required /></label>
                <label className="agreement-check"><input type="checkbox" checked={accountAgreed} onChange={(event) => setAccountAgreed(event.target.checked)} required /><span>同意 <Link to="/legal/terms" target="_blank" rel="noreferrer">用户协议</Link>、<Link to="/legal/privacy" target="_blank" rel="noreferrer">隐私政策</Link>和<Link to="/legal/children" target="_blank" rel="noreferrer">儿童信息说明</Link></span></label>
                {upgradeAccount.isError ? <p className="form-error" role="alert">{upgradeAccount.error.message}</p> : null}
                <button className="primary-button" type="submit" disabled={upgradeAccount.isPending || !accountAgreed}>{upgradeAccount.isPending ? "注册中…" : "注册并保留数据"}</button>
              </form>
            </>}
          </section>
        </div>
      ) : null}

      {detail ? (
        <div className="detail-backdrop" role="presentation" onMouseDown={(event) => event.target === event.currentTarget && setDetail(null)}>
          <section className="detail-sheet" role="dialog" aria-modal="true" aria-labelledby="detail-title">
            <button className="detail-close" type="button" aria-label="关闭" onClick={() => setDetail(null)}><X /></button>
            <span className="detail-icon">{detail.icon}</span>
            <h2 id="detail-title">{detail.title}</h2>
            {detail.content}
            <button className="primary-button detail-confirm" type="button" onClick={() => setDetail(null)}>知道了</button>
          </section>
        </div>
      ) : null}
    </section>
  );
}
